import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { it } from "date-fns/locale";

import { api } from "../lib/api";
import { useAuth } from "../stores/auth";
import { usePermissions } from "../stores/permissions";
import { TopBar } from "../components/ui/TopBar";
import { Icon, IconName } from "../components/ui/Icon";
import { SectionTitle } from "../components/ui/SectionTitle";
import { EventBadge } from "../components/ui/EventBadge";
import { ProductThumb } from "../components/ui/ProductThumb";
import { ManualProductModal } from "../components/products/ManualProductModal";

type Overview = {
  total_skus: number;
  total_units: number;
  low_stock_count: number;
  out_of_stock_count: number;
  events_today: number;
  open_transfers: number;
};

type EventRow = {
  id: number;
  event_type: string;
  sku: string;
  name: string | null;
  image_url: string | null;
  quantity_delta: number;
  operator: string | null;
  reason: string | null;
  notes: string | null;
  created_at: string;
};

type Transfer = {
  id: number;
  operator: string;
  status: string;
  opened_at: string;
};

type Tone = "navy" | "yellow" | "warn" | "pink";

type Kpi = {
  label: string;
  value: string;
  sub: string;
  icon: IconName;
  tone: Tone;
  to: string;
  hash?: string;
};

const TONE_ACCENT: Record<Tone, string> = {
  navy: "var(--tcg-navy)",
  yellow: "var(--tcg-yellow-deep)",
  warn: "var(--tcg-warn)",
  pink: "#BE185D",
};

function midnightIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function formatThousands(n: number): string {
  return n.toLocaleString("it-IT");
}

export function Home() {
  const user = useAuth((s) => s.user);
  const nav = useNavigate();
  const { hasPermission } = usePermissions();
  const [manualOpen, setManualOpen] = useState(false);
  const firstName = (user?.display_name?.split(" ")[0] || user?.username || "operatore").trim();

  const { data: overview } = useQuery<Overview>({
    queryKey: ["overview"],
    queryFn: async () => (await api.get("/api/stock/overview")).data,
    refetchInterval: 30_000,
  });

  const { data: todayEvents } = useQuery<EventRow[]>({
    queryKey: ["events", "today"],
    queryFn: async () =>
      (
        await api.get("/api/events", {
          params: { date_from: midnightIso(), page_size: 500 },
        })
      ).data,
    refetchInterval: 30_000,
  });

  const { data: recentEvents } = useQuery<EventRow[]>({
    queryKey: ["events", "recent"],
    queryFn: async () => (await api.get("/api/events", { params: { page_size: 10 } })).data,
    refetchInterval: 30_000,
  });

  const { data: openTransfers } = useQuery<Transfer[]>({
    queryKey: ["transfers", "all-open"],
    queryFn: async () =>
      (await api.get("/api/transfers", { params: { status: "open", page_size: 20 } })).data,
    refetchInterval: 30_000,
  });

  const kpis: Kpi[] = useMemo(() => {
    const today = todayEvents ?? [];
    const inToday = today.filter((e) => e.event_type === "stock_in").length;
    const transferToday = today.filter(
      (e) => e.event_type === "transfer_in" || e.event_type === "transfer_out",
    ).length;
    const lastSync = today.find(
      (e) =>
        e.event_type === "reconcile_check" ||
        e.event_type === "reconcile_correction" ||
        e.event_type === "tiktok_sale",
    );
    const tiktokSkuToday = today.filter((e) => e.event_type === "tiktok_sale").length;
    const operators = (openTransfers ?? [])
      .map((t) => t.operator)
      .filter((v, i, arr) => arr.indexOf(v) === i);
    const transferSub =
      operators.length === 0
        ? "Nessun viaggio aperto"
        : operators.slice(0, 3).map((o) => o[0]?.toUpperCase() + o.slice(1)).join(" · ");

    return [
      {
        label: "SKU in Sede",
        value: overview ? formatThousands(overview.total_skus) : "—",
        sub: overview ? `${formatThousands(overview.total_units)} pezzi totali` : "—",
        icon: "box",
        tone: "navy",
        to: "/inventario",
      },
      {
        label: "Eventi oggi",
        value: overview ? String(overview.events_today) : "—",
        sub: overview ? `${inToday} in entrata · ${transferToday} transfer` : "—",
        icon: "bolt",
        tone: "yellow",
        to: "/events?from=today",
      },
      {
        label: "Transfer aperti",
        value: overview ? String(overview.open_transfers) : "—",
        sub: transferSub,
        icon: "truck",
        tone: "warn",
        to: "/transfer-in",
      },
      {
        label: "Sync TikTok",
        value: lastSync ? format(new Date(lastSync.created_at), "HH:mm") : "—",
        sub: lastSync
          ? `oggi · ${tiktokSkuToday || today.filter((e) => e.event_type === "reconcile_check").length} SKU aggiornati`
          : "Nessun sync oggi",
        icon: "tiktok",
        tone: "pink",
        to: "/settings",
        hash: "#integrations",
      },
    ];
  }, [overview, todayEvents, openTransfers]);

  const greetingDate = format(new Date(), "EEEE d MMMM", { locale: it });
  const greetingDateCap = greetingDate.charAt(0).toUpperCase() + greetingDate.slice(1);

  const heroLine = (() => {
    const tr = overview?.open_transfers ?? 0;
    const ev = overview?.events_today ?? 0;
    if (tr > 0 && ev > 0) {
      return (
        <>
          Hai <strong style={{ color: "var(--tcg-yellow)" }}>{tr} transfer aperti</strong> e {ev} eventi oggi.
        </>
      );
    }
    if (tr > 0) {
      return (
        <>
          Hai <strong style={{ color: "var(--tcg-yellow)" }}>{tr} transfer aperti</strong> da chiudere.
        </>
      );
    }
    if (ev > 0) {
      return <>Hai {ev} eventi oggi. Tutto sotto controllo.</>;
    }
    return <>Tutto in ordine. Pronto per la prossima operazione.</>;
  })();

  return (
    <>
      <TopBar />

      <div style={{ padding: "14px 18px 8px" }}>
        <div
          style={{
            background: "var(--tcg-navy)",
            borderRadius: 22,
            padding: "18px 18px 16px",
            color: "var(--tcg-cream)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <img
            src="/assets/star.png"
            alt=""
            style={{
              position: "absolute",
              top: -18,
              right: -18,
              width: 110,
              opacity: 0.85,
              transform: "rotate(14deg)",
              pointerEvents: "none",
            }}
          />
          <div style={{ position: "relative" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--tcg-yellow)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              {greetingDateCap}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: "-0.02em", marginBottom: 4 }}>
              Ciao {firstName} 👋
            </div>
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.7)",
                fontWeight: 500,
                lineHeight: 1.5,
                maxWidth: 260,
              }}
            >
              {heroLine}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "8px 18px 0" }}>
        {kpis.map((k) => {
          const accent = TONE_ACCENT[k.tone];
          return (
            <button
              key={k.label}
              type="button"
              onClick={() => nav(k.to + (k.hash || ""))}
              className="tcg-card"
              style={{
                padding: 12,
                position: "relative",
                textAlign: "left",
                cursor: "pointer",
                fontFamily: "inherit",
                color: "inherit",
                transition: "transform .08s, box-shadow .15s",
              }}
              onPointerDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
              onPointerUp={(e) => (e.currentTarget.style.transform = "")}
              onPointerLeave={(e) => (e.currentTarget.style.transform = "")}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 8,
                    background: "var(--tcg-bg)",
                    color: accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon name={k.icon} size={14} />
                </div>
                <span style={{ color: "var(--tcg-muted-soft)" }}>
                  <Icon name="chev-r" size={12} />
                </span>
              </div>
              <div
                className="tcg-num"
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  color: "var(--tcg-ink)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                }}
              >
                {k.value}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: "var(--tcg-muted)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  marginTop: 4,
                }}
              >
                {k.label}
              </div>
              <div style={{ fontSize: 10, color: "var(--tcg-ink-soft)", marginTop: 3, fontWeight: 600 }}>{k.sub}</div>
            </button>
          );
        })}
      </div>

      <div style={{ padding: "14px 18px 0" }}>
        <Link
          to="/stock-in"
          className="tcg-btn tcg-btn-yellow tcg-btn-block"
          style={{ padding: "18px 18px", fontSize: 16, textDecoration: "none" }}
        >
          <Icon name="plus" size={18} /> Nuova operazione
        </Link>
        {hasPermission("manual_product_create") && (
          <button
            type="button"
            className="tcg-btn tcg-btn-secondary tcg-btn-block"
            style={{ marginTop: 8 }}
            onClick={() => setManualOpen(true)}
          >
            <Icon name="tag" size={16} /> Nuovo prodotto manuale
          </button>
        )}
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {(
            [
              { label: "Stock In", to: "/stock-in" },
              { label: "Stock Out", to: "/stock-out" },
              { label: "Transfer Out", to: "/transfer-out" },
              { label: "Transfer In", to: "/transfer-in" },
              { label: "Conta", to: "/inventario" },
            ] as const
          ).map((q) => (
            <Link
              key={q.label}
              to={q.to}
              className="tcg-chip"
              style={{ textDecoration: "none", padding: "6px 11px", fontSize: 11 }}
            >
              {q.label}
            </Link>
          ))}
        </div>
      </div>

      <SectionTitle
        right={
          <Link
            to="/events"
            style={{
              fontSize: 11,
              color: "var(--tcg-navy)",
              fontWeight: 700,
              paddingRight: 18,
              textDecoration: "none",
            }}
          >
            Vedi tutto →
          </Link>
        }
      >
        Ultimi eventi
      </SectionTitle>

      <div
        style={{
          background: "var(--tcg-card)",
          marginInline: 18,
          borderRadius: 16,
          border: "1px solid var(--tcg-border)",
          overflow: "hidden",
        }}
      >
        {(recentEvents ?? []).length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: "var(--tcg-muted)", textAlign: "center" }}>
            Nessun evento ancora.
          </div>
        )}
        {(recentEvents ?? []).map((e, i, arr) => {
          const delta = e.quantity_delta;
          const qtyText =
            delta === 0
              ? e.reason || "—"
              : `${delta > 0 ? "+" : "−"}${Math.abs(delta)}${e.reason ? " " + e.reason.split("_")[0] : ""}`;
          const qtyColor =
            delta > 0
              ? "var(--tcg-success)"
              : delta < 0
                ? "var(--tcg-danger)"
                : "var(--tcg-ink-soft)";
          return (
            <div
              key={e.id}
              style={{
                display: "flex",
                gap: 10,
                padding: "11px 12px",
                borderBottom: i === arr.length - 1 ? "none" : "1px solid var(--tcg-border)",
                alignItems: "flex-start",
              }}
            >
              <div
                className="tcg-mono"
                style={{
                  fontSize: 10,
                  color: "var(--tcg-muted)",
                  fontWeight: 700,
                  width: 34,
                  paddingTop: 2,
                  flexShrink: 0,
                }}
              >
                {format(new Date(e.created_at), "HH:mm")}
              </div>
              <EventBadge type={e.event_type} />
              <ProductThumb imageUrl={e.image_url} sku={e.sku} size="xs" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--tcg-ink)",
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {e.notes || e.name || labelForEvent(e)}
                </div>
                <div className="tcg-mono" style={{ fontSize: 10, color: "var(--tcg-muted)", marginTop: 2, fontWeight: 600 }}>
                  {e.sku} · {e.operator || "sistema"}
                </div>
              </div>
              <div
                className="tcg-num"
                style={{ fontSize: 11, fontWeight: 800, color: qtyColor, flexShrink: 0, paddingTop: 1 }}
              >
                {qtyText}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ height: 24 }} />

      <ManualProductModal open={manualOpen} onClose={() => setManualOpen(false)} />
    </>
  );
}

function labelForEvent(e: EventRow): string {
  switch (e.event_type) {
    case "stock_in":
      return "Stock in entrata";
    case "stock_out":
      return "Uscita straordinaria";
    case "transfer_out":
      return "Carico viaggio";
    case "transfer_in":
      return "Rientro viaggio";
    case "inventory_adjust":
      return "Rettifica inventario";
    case "tiktok_sale":
      return "Vendita TikTok Shop";
    case "shopify_sale":
      return "Vendita Shopify";
    case "reconcile_check":
      return "Sync piattaforme";
    case "reconcile_correction":
      return "Correzione sync";
    default:
      return e.event_type;
  }
}
